<template>
  <main class="container">
    <section>
      <h1 class="welcome-msg">Welcome, {{ userName && userName.split(' ')[0] }}!</h1>
      <HomePageUpdateWrapper />
      <h2 class="section-title">Instructional Design Tools</h2>
    </section>
    <section class="card-container">
      <InstructionalDesignCard
        accent-color="#733a95"
        hover-color="#4E1470"
        :img-src="homepageAssets.presentLevelsImg"
        :header="getPLHeader()"
        :card-description="`Find baseline assessments and write meaningful, data-driven present levels with strengths, needs, and ${effectsVisibility ? 'effect' : 'impact'} statements.`"
        :btn="{text: 'Present Levels Wizard', link: 'PresentLevelsNewWizard', query: {ref: 'home'}}"
        :other-links="[
          {text: 'Browse present levels', link: 'BrowsePresentLevelsPage', query: {ref: 'home'}},
          {text: 'Compose impact of disability', link: 'ImpactOfDisability', query: {ref: 'home'}, hideForNoIod: true},
          {text: 'Browse assessments', link: 'BrowseAssessments', query: {ref: 'home'}},
        ]"
      >
        <template v-if="betaVisibility" #header-button>
          <a :href="presentlevels.v2" class="pl-beta-link">
            <button class="btn" tabindex="-1">Try Beta Version</button>
          </a>
        </template>
      </InstructionalDesignCard>
      <InstructionalDesignCard
        accent-color="#00769f"
        hover-color="#04506A"
        :img-src="homepageAssets.goalsImg"
        :header="{line1: 'Develop', line2: 'Goals & Assessments'}"
        card-description="Write measurable, standard-aligned goals based on student need, and find resources to monitor progress toward goals."
        :btn="{text: 'Learning Goal Wizard', link: 'BrowseGoalsWizardModal', query: {ref: 'home'}}"
        :other-links="[
          {text: 'Browse goals', link: 'BrowseGoalsPage', query: {ref: 'home'}},
          {text: 'View goal index', link: 'GoalIndexPage', query: {ref: 'home'}},
          {text: 'Browse assessments', link: 'BrowseAssessments', query: {ref: 'home'}},
        ]"
      />
      <InstructionalDesignCard
        accent-color="#136320"
        hover-color="#064612"
        :img-src="homepageAssets.strategiesImg"
        :header="{line1: 'Discover', line2: 'Strategies & Supports'}"
        card-description="Implement IEPs with research-based strategies and accommodations for delivering specially designed instruction."
        :btn="{text: 'Strategy Wizard', link: 'StrategyWizard', query: {ref: 'home'}}"
        :other-links="[{text: 'Browse strategies', link: 'BrowseStrategiesPage', query: {ref: 'home'}}, {text: 'View strategy index', link: 'StrategyIndex', query: {ref: 'home'}}]"
      />
    </section>
    <section class="content-and-training-container">
      <div class="your-content-container">
        <h2 class="section-title">Your Content</h2>
        <RecentLibrary />
        <HomePageRecentlyViewed v-if="betaVisibility" />
        <RecentDownloads v-else />
      </div>
      <div class="training-center-container">
        <OnDemandTraining class="on-demand-training"/>
        <UpcomingWebinars class="upcoming-webinars"/>
        <RecentUpdates class="recent-updates"/>
      </div>
    </section>
  </main>
</template>

<script>
import { mapGetters } from 'vuex'
import InstructionalDesignCard from '@/components/home/InstructionalDesignCard'
import RecentLibrary from '@/components/home/RecentLibraryFolders'
import OnDemandTraining from '@/components/home/OnDemandTraining'
import UpcomingWebinars from '@/components/home/UpcomingWebinars'
import RecentUpdates from '@/components/home/RecentUpdates'
import RecentDownloads from '@/components/home/RecentDownloadsHome'
import HomePageRecentlyViewed from '@/components/home/HomePageRecentlyViewed'
import HomePageUpdateWrapper from '@/components/home/HomePageUpdateWrapper'
import { toolkitUrls } from '@/links'

export default {
  name: 'Home',
  components: {
      InstructionalDesignCard,
      RecentLibrary,
      UpcomingWebinars,
      OnDemandTraining,
      RecentUpdates,
      RecentDownloads,
      HomePageRecentlyViewed,
      HomePageUpdateWrapper
  },
  computed: {
      ...mapGetters('visibility', ['effectsVisibility']),
      ...mapGetters('user', ['userName']),
      ...mapGetters('visibility', ['betaVisibility'])
  },
  data() {
      return {
          ...toolkitUrls,
      }
  },
  methods: {
    getPLHeader() {
      return this.effectsVisibility
        ? {line1: 'Identify Present Levels &', line2: 'Effect Statements'}
        : {line1: 'Identify', line2: 'Present Levels'}
    },

  },
  created() {
      this.$sendEvent('Viewed Signed In Landing Page', { vue: true, ref: this.$route.query.ref })
  }
}
</script>

<style lang="less" scoped>
@import '~@/variables.less';

.container {
    font-family: 'IBM Plex Sans';
    font-size: 16px;
}
.welcome-msg, .section-title {
    color: @header-text-dark;
}
.welcome-msg {
    font-size: 2rem;
    margin-top: 0;
    font-weight: @font-weight-600;
}
.section-title {
    text-transform: uppercase;
    font-size: 1rem;
    font-weight: @font-weight-600;
}

.pl-beta-link {
  margin-left: var(--spacing-md);

  button {
    color: @plPurple;
    border-color: @plPurple;
    padding: var(--spacing-md) var(--spacing-xl);
    font-weight: var(--font-regular);
    border-radius: var(--radius-base);
    background-color: white;
    font-size: var(--text-md);
    white-space: nowrap;

    box-shadow: 0 2px 4px -2px rgba(0, 0, 0, 0.10), 0px 4px 6px -1px rgba(0, 0, 0, 0.10);
    transition: background-color 0.2s, color 0.2s;

    &:hover {
      background-color: @plPurple;
      color: white;
    }
  }
}

// Instructional Design Cards
.card-container {
    margin: 0 auto 1.5rem auto;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    grid-auto-rows: 1fr;
    gap: 0 20px;
    max-width:100%;

    @media (max-width: @screen-lg) {
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
    }

    @media (max-width: @screen-md) {
        grid-template-columns: 1fr;
        gap: 20px;
    }
}

// Content below the instructional design cards.
.content-and-training-container {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2.5rem;

  @media (max-width: @screen-lg) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto;
  }

  @media (max-width: @screen-md) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto auto;
  }
}

.training-center-container {
    display: grid;
    grid-template-rows: min-content min-content min-content;

    @media (min-width: @screen-sm) and (max-width: @screen-lg) {
        grid-template-columns: 2fr 1fr;
        grid-template-rows: min-content min-content;
        grid-template-areas:
            "onDemandTraining ."
            "recentUpdates webinars";

        .on-demand-training {
            grid-area: onDemandTraining;
        }
        .recent-updates {
            grid-area: recentUpdates;
        }
        .upcoming-webinars {
            grid-area: webinars;
        }
    }

    @media (max-width: @screen-xs-max) {
      grid-template-rows: min-content auto auto;
      grid-template-columns: 1fr;
    }
}
</style>
